import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

// Minimal, reusable phone input with dial code selector and emoji flags
// Props:
// - label?: string
// - value: string (combined number, e.g., +264811234567)
// - onChange: (combined: string) => void
// - required?: boolean
// - readOnly?: boolean
// - className?: string
// - selectClassName?: string
// - inputClassName?: string
// - defaultDialCode?: string (default '+264')
// - disabled?: boolean
// - id?: string
// - onInputFocus?: () => void

const COUNTRY_DIAL_CODES = [
  { name: 'Namibia', code: '+264', flag: '🇳🇦' },
  { name: 'South Africa', code: '+27', flag: '🇿🇦' },
  { name: 'Angola', code: '+244', flag: '🇦🇴' },
  { name: 'Botswana', code: '+267', flag: '🇧🇼' },
  { name: 'Zambia', code: '+260', flag: '🇿🇲' },
  { name: 'Zimbabwe', code: '+263', flag: '🇿🇼' },
  { name: 'Lesotho', code: '+266', flag: '🇱🇸' },
  { name: 'Eswatini', code: '+268', flag: '🇸🇿' },
  { name: 'Nigeria', code: '+234', flag: '🇳🇬' },
  { name: 'Kenya', code: '+254', flag: '🇰🇪' },
  { name: 'United Kingdom', code: '+44', flag: '🇬🇧' },
  { name: 'United States', code: '+1', flag: '🇺🇸' }
];
// Longest first, so +264 is tried before +27 etc.
const BY_LENGTH = [...COUNTRY_DIAL_CODES].sort((a, b) => b.code.length - a.code.length);

// Mobile numbers are 9 digits after the code; landlines 8. Up to 10 are kept
// so numbers from other countries are not cut short.
const MAX_LOCAL = 10;

// Splits any way a number may have been saved or typed — "+264 81 123 4567",
// "00264811234567", "264811234567", "081 123 4567", "811234567" — into the
// dial code and the local digits, without dropping any digit.
export const splitPhone = (raw, defaultDial = '+264') => {
  const text = String(raw || '').trim();
  let digits = text.replace(/\D/g, '');
  if (!digits) return { dial: defaultDial, local: '' };
  const international = text.startsWith('+') || text.startsWith('00');
  if (text.startsWith('00')) digits = digits.slice(2);
  const known = (d) => BY_LENGTH.find((c) => d.startsWith(c.code.slice(1)));
  if (international) {
    const m = known(digits);
    if (m) return { dial: m.code, local: digits.slice(m.code.length - 1).replace(/^0/, '') };
  } else if (digits.length >= 10 && !digits.startsWith('0')) {
    // A code written without the "+", followed by a full number.
    const m = BY_LENGTH.find((c) => c.code.length > 2 && digits.startsWith(c.code.slice(1)));
    if (m) return { dial: m.code, local: digits.slice(m.code.length - 1) };
  }
  // A local number: the leading trunk 0 is dropped (081… → 81…).
  return { dial: defaultDial, local: digits.replace(/^0/, '') };
};

// The combined value to store, e.g. "+264811234567" ('' when there is no number).
export const normalizePhone = (raw, defaultDial = '+264') => {
  const { dial, local } = splitPhone(raw, defaultDial);
  return local ? `${dial}${local.slice(0, MAX_LOCAL)}` : '';
};

// Digits after the dial code, for "is it filled in" checks.
export const phoneLocalDigits = (raw, defaultDial = '+264') => splitPhone(raw, defaultDial).local;

const formatLocal = (raw) => {
  const d = String(raw || '').replace(/\D/g, '').slice(0, MAX_LOCAL);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)} ${d.slice(2)}`;
  if (d.length === 10) return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
  return `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5)}`;
};

// Position in the formatted text just after the n-th digit.
const caretAfterDigits = (formatted, n) => {
  if (n <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i])) seen++;
    if (seen === n) return i + 1;
  }
  return formatted.length;
};

const PhoneInput = ({
  label,
  value,
  onChange,
  required = false,
  readOnly = false,
  className = '',
  selectClassName = '',
  inputClassName = '',
  defaultDialCode = '+264',
  disabled = false,
  id,
  onInputFocus
}) => {
  const [dial, setDial] = useState(defaultDialCode);
  const [local, setLocal] = useState('');
  const inputRef = useRef(null);
  const caretRef = useRef(null);

  // Parse incoming value into dial + local
  useEffect(() => {
    const parts = splitPhone(value, defaultDialCode);
    setDial(parts.dial);
    setLocal(formatLocal(parts.local));
  }, [value, defaultDialCode]);

  // Keep the cursor where the person was typing, not at the end.
  useLayoutEffect(() => {
    if (caretRef.current == null || !inputRef.current) return;
    const pos = caretAfterDigits(local, caretRef.current);
    caretRef.current = null;
    try { inputRef.current.setSelectionRange(pos, pos); } catch { /* not focusable */ }
  }, [local]);

  const digitsOf = (s) => String(s || '').replace(/\D/g, '').slice(0, MAX_LOCAL);

  const handleDialChange = (e) => {
    const newDial = e.target.value;
    setDial(newDial);
    const digits = digitsOf(local);
    onChange && onChange(digits ? newDial + digits : '');
  };

  const handleLocalChange = (e) => {
    const el = e.target;
    const raw = el.value;
    const caret = el.selectionStart ?? raw.length;
    let digits = raw.replace(/\D/g, '');
    let digitsBeforeCaret = raw.slice(0, caret).replace(/\D/g, '').length;
    // Backspace over a space: remove the digit before it instead of doing nothing.
    if (digits === digitsOf(local) && raw.length < local.length && digitsBeforeCaret > 0) {
      digits = digits.slice(0, digitsBeforeCaret - 1) + digits.slice(digitsBeforeCaret);
      digitsBeforeCaret -= 1;
    }
    // A pasted full number (+264…, 081…) is split, not cut short.
    if (/^\s*(\+|00)/.test(raw) || (digits.length > MAX_LOCAL)) {
      const parts = splitPhone(raw, dial);
      digits = parts.local;
      if (parts.dial !== dial) setDial(parts.dial);
      digitsBeforeCaret = digits.length;
      digits = digits.slice(0, MAX_LOCAL);
      caretRef.current = Math.min(digitsBeforeCaret, digits.length);
      setLocal(formatLocal(digits));
      onChange && onChange(digits ? parts.dial + digits : '');
      return;
    }
    digits = digits.slice(0, MAX_LOCAL);
    caretRef.current = Math.min(digitsBeforeCaret, digits.length);
    setLocal(formatLocal(digits));
    onChange && onChange(digits ? dial + digits : '');
  };

  if (readOnly) {
    const displayValue = (local && local.replace(/\D/g, '').length)
      ? `${dial} ${local}`
      : (value || '');
    return (
      <div className={className}>
        {label && (
          <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1" htmlFor={id}>{label}</label>
        )}
        <input
          id={id}
          type="text"
          value={displayValue}
          readOnly
          className={`w-full p-3 ${inputClassName}`}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1" htmlFor={id}>{label}</label>
      )}
      <div className="grid grid-cols-3 gap-2">
        <select
          id={id ? `${id}-dial` : undefined}
          value={dial}
          onChange={handleDialChange}
          onFocus={onInputFocus}
          className={`col-span-1 w-full p-3 ${selectClassName}`}
          disabled={disabled}
        >
          {COUNTRY_DIAL_CODES.map(c => (
            <option key={c.code} value={c.code}>{`${c.flag} ${c.name} (${c.code})`}</option>
          ))}
        </select>
        <input
          ref={inputRef}
          id={id}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          value={local}
          onChange={handleLocalChange}
          onFocus={onInputFocus}
          placeholder="81 234 5678"
          maxLength={16}
          className={`col-span-2 w-full p-3 ${inputClassName}`}
          required={required}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export default PhoneInput;
